import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Sparkles, Check, Zap, ExternalLink, BookOpen, RotateCcw, ShieldCheck } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { appleIapAPI } from '../services/apiService';
import { isIosApp } from '../utils/appPlatform';

const API_URL = import.meta.env.VITE_API_URL;
const STRIPE_MONTHLY_LINK = import.meta.env.VITE_STRIPE_MONTHLY_LINK;
const STRIPE_YEARLY_LINK = import.meta.env.VITE_STRIPE_YEARLY_LINK;
const APPLE_MONTHLY_ID = 'com.integrativehealthjournal.nutritrack.pro.monthly';
const APPLE_YEARLY_ID = 'com.integrativehealthjournal.nutritrack.pro.yearly';

const PRO_FEATURES = [
  'Food diary, macro calculator, and progress reports',
  'AI Nutrition Coach — unlimited messages',
  'Upload nutrition label photos for analysis',
  '"Should I eat this?" AI analysis',
  'AI builds your grocery list from your store',
  'AI finds & saves recipes to your library',
  'Log meals by just describing them',
  'Kimi K2.6 with live web search',
];

const UpgradePage = () => {
  const [status, setStatus] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [appleProducts, setAppleProducts] = useState([]);
  const [appleConnected, setAppleConnected] = useState(false);
  const [appleBusy, setAppleBusy] = useState('');
  const [appleMessage, setAppleMessage] = useState('');
  const [appleSuccess, setAppleSuccess] = useState(false);
  const { user, refreshUser } = useAuth();
  const location = useLocation();
  const iosApp = isIosApp();

  const isBookUser = user?.subscriptionType === 'book_lifetime' || user?.authType === 'kdp';
  const hasPro = user?.subscriptionStatus === 'pro' || user?.subscriptionStatus === 'premium' || status === 'pro';

  const token = localStorage.getItem('authToken');
  const headers = { Authorization: `Bearer ${token}` };

  const params = new URLSearchParams(location.search);
  const success = params.get('success');
  const canceled = params.get('canceled');

  useEffect(() => {
    fetch(`${API_URL}/payments/status`, { headers })
      .then(r => r.json())
      .then(d => setStatus(d.status))
      .catch(() => setStatus('free'));
  }, [success]);

  useEffect(() => {
    if (!iosApp) return undefined;

    const verifyApplePurchase = async (purchase) => {
      setAppleBusy(purchase.productId || 'restore');
      setAppleMessage('Confirming your subscription with Apple...');
      try {
        const verified = await appleIapAPI.verify(purchase);
        window.ReactNativeWebView?.postMessage(JSON.stringify({
          type: 'IAP_FINISH_TRANSACTION',
          transactionId: verified.transactionId || purchase.transactionId,
        }));
        await refreshUser();
        setStatus('pro');
        setAppleSuccess(true);
        setAppleMessage('NutriTrack Pro is active on this account.');
      } catch (error) {
        setAppleMessage(error.message || 'Apple confirmed the purchase, but access could not be activated. Please try Restore Purchases.');
      } finally {
        setAppleBusy('');
      }
    };

    const handleAppleEvent = (event) => {
      const detail = event.detail || {};
      if (detail.type === 'IAP_PRODUCTS') {
        setAppleConnected(Boolean(detail.connected));
        setAppleProducts(detail.products || []);
      }
      if (detail.type === 'IAP_PURCHASE_SUCCESS' && detail.purchase) {
        void verifyApplePurchase(detail.purchase);
      }
      if (detail.type === 'IAP_RESTORE_EMPTY') {
        setAppleBusy('');
        setAppleMessage('No active NutriTrack Pro purchase was found for this Apple Account.');
      }
      if (detail.type === 'IAP_ERROR') {
        setAppleBusy('');
        setAppleMessage(detail.message || 'The Apple purchase could not be completed.');
      }
    };

    window.addEventListener('nutritrack:iap', handleAppleEvent);
    window.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'IAP_GET_PRODUCTS' }));
    return () => window.removeEventListener('nutritrack:iap', handleAppleEvent);
  }, [iosApp, refreshUser]);

  const sendAppleMessage = (message) => {
    if (!window.ReactNativeWebView) {
      setAppleMessage('Apple purchases are available in the NutriTrack iPhone and iPad app.');
      return false;
    }
    window.ReactNativeWebView.postMessage(JSON.stringify(message));
    return true;
  };

  const handleApplePurchase = (productId) => {
    setAppleMessage('');
    if (sendAppleMessage({ type: 'IAP_PURCHASE', productId })) {
      setAppleBusy(productId);
    }
  };

  const handleRestorePurchases = () => {
    setAppleMessage('Checking your Apple Account...');
    if (sendAppleMessage({ type: 'IAP_RESTORE' })) {
      setAppleBusy('restore');
    }
  };

  const handlePortal = async () => {
    if (iosApp) {
      sendAppleMessage({ type: 'IAP_MANAGE_SUBSCRIPTION' });
      return;
    }
    setPortalLoading(true);
    try {
      const res = await fetch(`${API_URL}/payments/portal`, { method: 'POST', headers });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
    } catch {
      alert('Could not open billing portal.');
    } finally {
      setPortalLoading(false);
    }
  };

  const monthlyProduct = appleProducts.find(product => product.id === APPLE_MONTHLY_ID);
  const yearlyProduct = appleProducts.find(product => product.id === APPLE_YEARLY_ID);
  const monthlyPrice = monthlyProduct?.displayPrice || '$4.99';
  const yearlyPrice = yearlyProduct?.displayPrice || '$49.99';

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 px-4 py-1.5 rounded-full text-sm font-medium mb-4">
          <Sparkles className="w-4 h-4" /> {iosApp ? '3-Day Free Trial' : 'Upgrade to Pro'}
        </div>
        <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white mb-3">
          {iosApp ? 'Try Everything Free for 3 Days' : 'Unlock the AI Nutrition Coach'}
        </h1>
        <p className="text-gray-500 dark:text-gray-400 max-w-xl mx-auto">
          {iosApp
            ? 'Choose a plan and confirm with Apple to start. You will not be charged during the trial, and you can cancel in your Apple Account before it ends.'
            : 'Get unlimited access to your personal AI coach — powered by Kimi K2.6 with live web search. Scan labels, build grocery lists, find recipes, and log meals just by describing them.'}
        </p>
      </div>

      {(success || appleSuccess) && (
        <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-xl text-center text-green-700 dark:text-green-400 font-medium">
          Welcome to Pro! Your AI Nutrition Coach is now unlocked.
        </div>
      )}
      {canceled && (
        <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-xl text-center text-yellow-700 dark:text-yellow-400">
          Checkout canceled. You can upgrade anytime.
        </div>
      )}

      {isBookUser ? (
        <div className="mb-6 p-6 bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-200 dark:border-amber-800 rounded-2xl text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-amber-100 dark:bg-amber-900/40 rounded-full mb-3">
            <BookOpen className="w-6 h-6 text-amber-600 dark:text-amber-400" />
          </div>
          <h2 className="text-xl font-bold text-amber-900 dark:text-amber-300 mb-1">Lifetime Pro Access</h2>
          <p className="text-amber-700 dark:text-amber-400 font-medium">
            Thank you for purchasing the companion book! Your lifetime Pro access is active.
          </p>
        </div>
      ) : hasPro && !success && (
        <div className="mb-6 p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-700 rounded-xl text-center text-indigo-700 dark:text-indigo-300 font-medium">
          ✅ You're on Pro! Full AI Nutrition Coach access is active.
        </div>
      )}

      <div className={`grid gap-6 mb-8 ${iosApp ? 'max-w-xl mx-auto' : 'md:grid-cols-2'}`}>
        {/* Free */}
        {!iosApp && <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6">
          <div className="mb-4">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Free</h2>
            <div className="text-3xl font-extrabold text-gray-900 dark:text-white mt-1">$0<span className="text-base font-normal text-gray-500">/mo</span></div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Everything you need to track nutrition</p>
          </div>
          <ul className="space-y-2.5 mb-6">
            {FREE_FEATURES.map(f => (
              <li key={f} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <Check className="w-4 h-4 text-green-500 flex-shrink-0" /> {f}
              </li>
            ))}
          </ul>
          {!hasPro && (
            <div className="w-full py-2.5 text-center text-sm font-medium text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-600 rounded-lg">
              Current plan
            </div>
          )}
        </div>}

        {/* Pro */}
        <div className="bg-gradient-to-br from-indigo-600 to-blue-600 rounded-2xl p-6 text-white relative overflow-hidden">
          <div className="absolute top-3 right-3 bg-yellow-400 text-yellow-900 text-xs font-bold px-2 py-0.5 rounded-full">
            BEST VALUE
          </div>
          <div className="mb-4">
            <div className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-yellow-300" />
              <h2 className="text-lg font-bold">Pro</h2>
            </div>
            <div className="text-3xl font-extrabold mt-1">
              {iosApp ? 'Free for 3 days' : '$4.99'}
              {!iosApp && <span className="text-base font-normal text-blue-200">/mo</span>}
            </div>
            <p className="text-sm text-blue-200 mt-1">
              {iosApp ? `Then ${monthlyPrice}/month or ${yearlyPrice}/year` : 'Unlimited AI coach access'}
            </p>
          </div>
          <ul className="space-y-2.5 mb-6">
            {PRO_FEATURES.map(f => (
              <li key={f} className="flex items-center gap-2 text-sm text-blue-100">
                <Check className="w-4 h-4 text-yellow-300 flex-shrink-0" /> {f}
              </li>
            ))}
          </ul>

          {isBookUser ? (
            <div className="w-full py-3 bg-white/20 text-white font-bold rounded-xl text-center text-sm border border-white/30">
              Lifetime Access Unlocked
            </div>
          ) : hasPro ? (
            <button
              onClick={handlePortal}
              disabled={portalLoading}
              className="w-full py-3 bg-white/20 hover:bg-white/30 text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2 text-sm"
            >
              <ExternalLink className="w-4 h-4" />
              {portalLoading ? 'Opening...' : iosApp ? 'Manage Apple Subscription' : 'Manage Subscription'}
            </button>
          ) : iosApp ? (
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => handleApplePurchase(APPLE_MONTHLY_ID)}
                disabled={!appleConnected || Boolean(appleBusy)}
                className="w-full py-3 bg-white text-indigo-700 font-bold rounded-xl hover:bg-blue-50 disabled:opacity-60 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                <Sparkles className="w-4 h-4" />
                {appleBusy === APPLE_MONTHLY_ID ? 'Opening Apple...' : `Start 3-Day Trial - ${monthlyPrice}/month`}
              </button>
              <button
                type="button"
                onClick={() => handleApplePurchase(APPLE_YEARLY_ID)}
                disabled={!appleConnected || Boolean(appleBusy)}
                className="w-full py-2.5 bg-white/20 hover:bg-white/30 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2 text-sm"
              >
                <Zap className="w-4 h-4 text-yellow-300" />
                {appleBusy === APPLE_YEARLY_ID ? 'Opening Apple...' : `Start 3-Day Trial - ${yearlyPrice}/year`}
              </button>
              <button
                type="button"
                onClick={handleRestorePurchases}
                disabled={Boolean(appleBusy)}
                className="w-full py-2.5 text-blue-100 hover:text-white disabled:opacity-60 font-semibold transition-colors flex items-center justify-center gap-2 text-sm"
              >
                <RotateCcw className="w-4 h-4" />
                {appleBusy === 'restore' ? 'Restoring...' : 'Restore Purchases'}
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              <a
                href={STRIPE_MONTHLY_LINK}
                className="w-full py-3 bg-white text-indigo-600 font-bold rounded-xl hover:bg-blue-50 transition-colors flex items-center justify-center gap-2"
              >
                <Sparkles className="w-4 h-4" />
                Monthly — $4.99/mo
              </a>
              <a
                href={STRIPE_YEARLY_LINK}
                className="w-full py-2.5 bg-white/20 hover:bg-white/30 text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2 text-sm"
              >
                <Zap className="w-4 h-4 text-yellow-300" />
                Yearly — $49.99 (save 17%)
              </a>
            </div>
          )}
        </div>
      </div>

      {iosApp && appleMessage && (
        <div className={`mb-5 rounded-lg border px-4 py-3 text-center text-sm font-medium ${appleSuccess ? 'border-green-200 bg-green-50 text-green-700' : 'border-blue-200 bg-blue-50 text-blue-800'}`} role="status">
          {appleMessage}
        </div>
      )}

      <p className="text-center text-xs text-gray-400 dark:text-gray-500">
        {iosApp ? (
          <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> Apple will ask you to confirm the subscription and payment method. No charge for 3 days; then your selected plan renews automatically until canceled in your Apple Account.</span>
        ) : 'Payments processed securely by Stripe. Cancel anytime from your billing portal.'}
      </p>
      {iosApp && (
        <p className="mt-3 text-center text-xs text-gray-500 dark:text-gray-400">
          By continuing, you agree to the <Link to="/terms" className="font-semibold text-indigo-700 underline dark:text-indigo-300">Terms of Use</Link> and acknowledge the <Link to="/privacy" className="font-semibold text-indigo-700 underline dark:text-indigo-300">Privacy Policy</Link>.
        </p>
      )}
    </div>
  );
};

export default UpgradePage;
